import { useEffect, useRef } from "react";

import { num } from "../format";
import type { SocketStatus } from "../hooks";
import type { State } from "../store";
import { isPending } from "./Feed";
import { DensityToggle } from "./DensityToggle";
import { Brand, Icons } from "./Icons";
import { openMiniWindow } from "./MiniShell";
import { Popover } from "./Popover";
import { ThemePicker } from "./ThemePicker";

interface Props {
  state: State;
  socketStatus: SocketStatus;
  onLock: (locked: boolean) => void;
  onSignOut: () => void;
  onOpenPalette: () => void;
  onOpenSettings?: () => void;
}

const SHORTCUT =
  typeof navigator !== "undefined" && /Mac|iP(hone|ad|od)/.test(navigator.platform)
    ? "⌘K"
    : "Ctrl+K";

export function Header({
  state,
  socketStatus,
  onLock,
  onSignOut,
  onOpenPalette,
  onOpenSettings,
}: Props) {
  const queued = state.history.filter(isPending).length;
  /* A dash, not a zero and not "?": before the first bootstrap answers we do
     not know the balance, and "credits ?" reads as something being broken. */
  const credits = state.subscription
    ? state.subscription.limit
      ? num(state.subscription.remaining)
      : num(state.subscription.used)
    : "—";

  const admin = state.me?.admin ?? false;
  const headerRef = useHeaderOffset();

  return (
    <header ref={headerRef}>
      <Brand name={state.tenant.display_name} />
      <span className="divider"></span>
      {/* display:contents on desktop, so these sit in the header flow as usual;
          on mobile the wrapper becomes its own scrollable row. */}
      <div className="statusbar">
        <span className="pill">
          <span className={`dot ${socketStatus === "live" ? "on" : "off"}`}></span>
          <span>{socketStatus === "live" ? "live" : socketStatus}</span>
        </span>
        <span className="pill">
          queue <b>{queued}</b>
        </span>
        <span className="pill" title="ElevenLabs characters left this billing period">
          credits <b>{credits}</b>
        </span>
        {/* Overlay count and who is online were two more pills competing with
            these three. They are the answer to one question — "who is
            connected?" — so they are one control that opens on demand. */}
        <ConnectionsPill
          overlays={state.overlays}
          online={state.online}
          me={state.me?.name ?? ""}
        />
      </div>
      <span className="spacer"></span>

      {/* The kill switch, where the indicator used to be.

          It lived three clicks deep — right rail, Admin card, Controls tab —
          while the header carried a pill that could only *tell* you TTS was
          locked. Something pressed when a stream is going wrong belongs at the
          top level, and the state and the control are the same fact. */}
      {admin ? (
        <button
          className={"lock-toggle" + (state.locked ? " on" : "")}
          aria-pressed={state.locked}
          onClick={() => onLock(!state.locked)}
        >
          <span className={`dot ${state.locked ? "off" : "on"}`}></span>
          {state.locked ? "TTS locked" : "Lock TTS"}
        </button>
      ) : (
        state.locked && (
          <span className="pill outline" id="lock-pill">
            TTS locked
          </span>
        )
      )}

      <button
        className="ghost palette-button"
        onClick={onOpenPalette}
      >
        Commands <kbd>{SHORTCUT}</kbd>
      </button>

      <ThemePicker />
      <DensityToggle />
      <button
        type="button"
        className="ghost icon-only"
        title="Mini panel"
        aria-label="Open the mini panel"
        onClick={openMiniWindow}
      >
        {Icons.popout}
      </button>

      {admin && onOpenSettings && (
        <button
          className="ghost header-settings-btn"
          onClick={onOpenSettings}
          title="Instance settings"
          aria-label="Instance settings"
        >
          {Icons.settings}
          <span>Settings</span>
        </button>
      )}

      <span className="pill">
        {/* Its own element so the narrowest phones can drop the word and keep
            the name, which is the half that answers "who am I signed in as". */}
        <span className="you-label">you </span>
        <b id="me-name">
          {state.me?.name}
          {state.me?.admin ? " (admin)" : ""}
        </b>
      </span>
      <button className="ghost" onClick={onSignOut}>
        Sign out
      </button>
    </header>
  );
}

/* Publish the header's real height so the things pinned under it know where it
   ends.

   `--header-h` is a constant, and the header is a wrapping flex row: it is one
   line on a desktop, three on a phone, and two at the awkward widths in
   between. The connection banner and the now-playing card stick to that
   constant, so at every width where the header had wrapped they pinned
   themselves *behind* it — the banner announcing a dropped socket was the thing
   most likely to be hidden.

   Written to a second variable rather than back into `--header-h`, which is
   also this element's `min-height`: measuring an element into its own minimum
   is a loop that latches at the tallest value it ever saw. */
function useHeaderOffset() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const header = ref.current;
    if (!header) return;

    const publish = () => {
      document.documentElement.style.setProperty(
        "--header-offset",
        `${Math.round(header.getBoundingClientRect().height)}px`,
      );
    };
    publish();

    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--header-offset");
    };
  }, []);

  return ref;
}

function ConnectionsPill({
  overlays,
  online,
  me,
}: {
  overlays: number;
  online: string[];
  me: string;
}) {
  return (
    <Popover
      className="pill pill-button"
      ariaLabel="Who is connected"
      label={
        <>
          <span className={`dot ${overlays ? "on" : "off"}`}></span>
          connected <b>{online.length + overlays}</b>
        </>
      }
    >
      {() => (
        <>
          <div className="field-label">Overlays</div>
          <div className="rows">
            <div>
              <span className={overlays ? "" : "muted"}>
                {overlays === 0
                  ? "No OBS overlay is connected"
                  : `${overlays} browser source${overlays === 1 ? "" : "s"}`}
              </span>
            </div>
          </div>
          <div className="field-label" style={{ marginTop: 12 }}>
            In the panel
          </div>
          <div className="rows">
            {online.length === 0 ? (
              <span className="muted">nobody</span>
            ) : (
              online.map((name) => (
                <div key={name}>
                  <span>
                    {name}
                    {name === me ? " (you)" : ""}
                  </span>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </Popover>
  );
}
